import { isArtifactEntityType } from '@bower/shared/constants/artifact';
import type { HierarchyNodeType } from '@bower/shared/types/api';
import type { DropPosition } from '../components/navigation/HierarchyTree.dnd';

/**
 * Client-side validation for hierarchy drag-and-drop operations
 * Mirrors backend logic from entity-relationship.service.ts
 *
 * Business rules:
 * - Projects: Can have workspace OR project as parent
 * - Artifacts: Can have workspace OR project as parent
 */

export type DraggableEntityType = 'project' | 'artifact';
export type DropTargetType = 'workspace' | 'project' | 'artifact';

interface ValidParentTypesResult {
  validTypes: DropTargetType[];
  reason: string;
}

/**
 * Get valid parent types for a given entity type
 */
export function getValidParentTypes(entityType: DraggableEntityType): ValidParentTypesResult {
  switch (entityType) {
    case 'project':
      return {
        validTypes: ['workspace', 'project'],
        reason: 'Projects can be top-level (workspace parent) or sub-projects (project parent)',
      };
    case 'artifact':
      return {
        validTypes: ['workspace', 'project'],
        reason: 'Artifacts can be attached to workspace or project',
      };
    default:
      return {
        validTypes: [],
        reason: `Unknown entity type: ${entityType}`,
      };
  }
}

/**
 * Check if a parent type is valid for the given entity type
 */
export function isValidParentType(
  entityType: DraggableEntityType,
  parentType: DropTargetType,
): boolean {
  const { validTypes } = getValidParentTypes(entityType);
  return validTypes.includes(parentType);
}

/**
 * Check if a node is an artifact type (note, attachment, protocol, or generic artifact)
 * Delegates to the shared registry so new artifact types are picked up automatically.
 */
export function isArtifactNode(nodeType: HierarchyNodeType | string): boolean {
  return isArtifactEntityType(nodeType);
}

/**
 * Check if dragging an entity onto a target is valid
 */
export function canDropOnTarget(
  draggedNodeType: HierarchyNodeType,
  draggedNodeId: string,
  targetNodeType: HierarchyNodeType,
  targetNodeId: string,
  /**
   * Where in the row the pointer is. Only `'on'` means "make the target the
   * new parent"; `'before'` / `'after'` mean "become the target's SIBLING", so
   * the destination is the target's parent and the target's own type is
   * irrelevant.
   *
   * Defaults to `'on'` — the strict reading — so an existing caller that
   * doesn't know the position keeps today's behaviour.
   */
  position: DropPosition = 'on',
): boolean {
  // Can't drop on itself
  if (draggedNodeId === targetNodeId) {
    return false;
  }

  // Sibling drop. A target's parent is always a workspace or a project (both
  // projects and artifacts only ever hang off those), and both draggable types
  // are legal under both — so if the target can live there, so can the dragged
  // node. Judging this against the TARGET's type is what made dropping an
  // artifact between two artifacts inside a collection fail with "Cannot move
  // artifact to artifact", when the user was aiming at the collection.
  if (position !== 'on') {
    return draggedNodeType === 'project' || isArtifactNode(draggedNodeType);
  }

  // Workspace nodes (synthetic "Artifacts" folder) can't be dropped
  if (draggedNodeType === 'workspace') {
    return false;
  }

  // Convert HierarchyNodeType to entity types
  let entityType: DraggableEntityType;
  if (draggedNodeType === 'project') {
    entityType = 'project';
  } else if (isArtifactNode(draggedNodeType)) {
    entityType = 'artifact';
  } else {
    return false;
  }

  // Convert target type
  let parentType: DropTargetType;
  if (targetNodeType === 'workspace' || targetNodeType === 'project') {
    parentType = targetNodeType;
  } else if (isArtifactNode(targetNodeType)) {
    // Can't drop onto artifacts - they're leaf nodes
    return false;
  } else {
    return false;
  }

  return isValidParentType(entityType, parentType);
}
